import React from 'react';
import { Shield, Letter, Heart } from '@solar-icons/react';
import { useTranslation } from 'react-i18next';

const LegacyVaultHero: React.FC = () => {
    const { t } = useTranslation();
    const handleCTAClick = () => {
        const pricingElement = document.getElementById('pricing');
        if (pricingElement) {
            pricingElement.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <section id="legacy-vault-hero" className="relative overflow-x-hidden pt-16 lg:pt-20">
            <div className="relative mx-auto max-w-[1142px] px-4">
                {/* Hero Content Section */}
                <div className="pb-12 lg:pb-16">
                    <div className="relative mx-auto max-w-[852px] pb-16 text-center">
                        {/* Background Element */}
                        <img src="/assets/images/legacy-vault-hero-bg.png" alt="Legacy Vault Hero Background" className="absolute left-1/2 top-0 w-full max-w-[1110px] -translate-x-1/2" />

                        {/* Main Headline */}
                        <h1 className="relative mb-8 text-4xl font-extrabold leading-tight text-green-700 dark:text-white md:mb-8 lg:px-10 lg:text-[56px]" data-aos="fade-up" data-aos-duration="1000">
                            <span className="relative z-10">{t('lv_page.hero.headline')}</span>
                            <img src="/assets/images/legacy-vault-green-arrow.svg" alt="Decorative Arrow" className="absolute -top-5 end-40 hidden rtl:rotate-y-180 lg:inline-block" />
                        </h1>

                        {/* Subheadline */}
                        <p className="relative z-10 text-lg md:text-22 text-gray-600 dark:text-gray-300" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="200">
                            {t('lv_page.hero_subheadline')}
                        </p>

                        {/* CTA Button */}
                        <div className="relative z-10 mt-12" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="400">
                            <button
                                id="legacyVaultCta"
                                className="inline-flex py-[15px] px-6 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors"
                                onClick={handleCTAClick}
                            >
                                {t('lv_page.hero.cta_button')}
                            </button>
                        </div>
                    </div>

                    {/* Visual Grid Section */}
                    <div className="grid gap-8 sm:grid-cols-2 xl:h-[600px]">
                        <div className="overflow-hidden rounded-2xl xl:row-span-2 border-4 border-green-200 dark:border-green-800" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="600">
                            <div className="relative h-full w-full bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-8 flex flex-col justify-center items-center">
                                <div className="text-center">
                                    <div className="mb-6 text-6xl">
                                        <Shield size={48} color="#2a6355" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-green-700 dark:text-white mb-4">{t('lv_page.hero.secure_your_legacy')}</h3>
                                    <p className="text-gray-600 dark:text-gray-300">{t('lv_page.hero.bank_level_encryption')}</p>
                                </div>
                            </div>
                        </div>
                        <div className="overflow-hidden rounded-2xl border-4 border-green-200 dark:border-green-800" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="800">
                            <div className="relative h-full w-full bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-6 flex flex-col justify-center items-center">
                                <div className="text-center">
                                    <div className="mb-4 text-4xl">
                                        <Letter size={32} color="#2a6355" />
                                    </div>
                                    <h3 className="text-xl font-bold text-blue-700 dark:text-white mb-2">{t('lv_page.hero.preserve_your_voice')}</h3>
                                    <p className="text-gray-600 dark:text-gray-300 text-sm">{t('lv_page.hero.leave_more_than')}</p>
                                </div>
                            </div>
                        </div>
                        <div className="overflow-hidden rounded-2xl border-4 border-green-200 dark:border-green-800" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="1000">
                            <div className="relative h-full w-full bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-6 flex flex-col justify-center items-center">
                                <div className="text-center">
                                    <div className="text-center">
                                        <div className="mb-4 text-4xl">
                                            <Heart size={32} color="#f38173" />
                                        </div>
                                        <h3 className="text-xl font-bold text-purple-700 dark:text-white mb-2">{t('lv_page.hero.protect_your_family')}</h3>
                                        <p className="text-gray-600 dark:text-gray-300 text-sm">{t('lv_page.hero.spare_them_300')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default LegacyVaultHero;
